import { useEffect, useRef, useState } from 'react';

export interface UrlPrefillFilterOption {
  value: string;
  label: string;
}

// Corporate-suffix/conjunction words dropped before matching, so a brand name like
// "Blue Cross Blue Shield" still matches a backend label such as "Anthem Blue Cross
// and Blue Shield HP", and "United Healthcare" matches "UnitedHealth Group, Inc.".
const STOPWORDS = new Set([
  'and', 'of', 'the', 'inc', 'incorporated', 'corp', 'corporation', 'group',
  'llc', 'ltd', 'limited', 'company', 'co', 'holding', 'holdings', 'hp',
]);

const normalize = (value: string) =>
  value
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word && !STOPWORDS.has(word))
    .join('');

/**
 * Selects a filter option once, matched against a URL query param (e.g. a
 * marketing link's `?carrier=aetna`) by exact value or label, ignoring case
 * and punctuation. Waits for `options` to load and skips if the field
 * already has a value (e.g. restored from a saved quote).
 */
export function useUrlPrefillFilter(
  paramName: string,
  options: UrlPrefillFilterOption[],
  currentValues: string[],
  onMatch: (value: string) => void
) {
  const [paramValue] = useState(() => new URLSearchParams(window.location.search).get(paramName));
  const applied = useRef(false);

  useEffect(() => {
    if (!paramValue || applied.current) return;
    if (currentValues.length > 0) {
      applied.current = true;
      return;
    }
    if (!options.length) return;

    const target = normalize(paramValue);
    const match = options.find((o) => {
      const value = normalize(o.value);
      const label = normalize(o.label);
      return value === target || label === target || label.includes(target) || target.includes(label);
    });
    if (match) {
      applied.current = true;
      onMatch(match.value);
    }
  }, [paramValue, options, currentValues, onMatch]);
}
